import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { generateStrategyAsync, getAnalystChats, getAnalystChat, deleteAnalystChat, updateAnalystChat } from '../api';
import { useAuth } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import EarningsFundamentalsCard from '../components/EarningsFundamentalsCard';
import ExportStrategyModal from '../components/ExportStrategyModal';
import { track, Events } from '../analytics';

const WELCOME_MSG = {
  role: 'assistant',
  content:
    "Welcome. I'm your AI research analyst. Ask me anything about stocks, market conditions, " +
    "technical setups, or trading strategies \u2014 I'll pull live data and give you a Wall Street-grade " +
    "analysis.\n\nTry something like:\n- *\"Is now a good time to buy Apple?\"*\n- *\"Compare NVDA vs AMD technically\"*\n- *\"What's the RSI saying about Tesla?\"*",
};

export default function AnalystPage({ onStrategyCreated, onRequiresSignup, onFeedback }) {
  const { isAuthenticated } = useAuth();

  // Chat state
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [suggestStrategy, setSuggestStrategy] = useState(false);
  const [exportTarget, setExportTarget] = useState(null); // { graph, name } for the export modal
  const [generatingStrategy, setGeneratingStrategy] = useState(false);
  const [buildModalOpen, setBuildModalOpen] = useState(false); // "being built, check notifications"
  const [chatId, setChatId] = useState(null);
  const [engagementOptions, setEngagementOptions] = useState(null);
  const bottomRef = useRef(null);
  const genPollRef = useRef(null);
  const lastTickersRef = useRef([]);
  const usedSourcesRef = useRef(new Set());

  // Sidebar state
  const [chatList, setChatList] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');

  // Load chat list on mount
  useEffect(() => {
    if (isAuthenticated) loadChatList();
  }, [isAuthenticated]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const loadChatList = async () => {
    setLoadingChats(true);
    try {
      const { data } = await getAnalystChats();
      setChatList(data);
    } catch {
      // silent
    } finally {
      setLoadingChats(false);
    }
  };

  const startNewChat = () => {
    setChatId(null);
    setMessages([WELCOME_MSG]);
    setSuggestStrategy(false);
    setEngagementOptions(null);
    setInput('');
    lastTickersRef.current = [];
    usedSourcesRef.current = new Set();
  };

  const loadChat = async (id) => {
    try {
      const { data } = await getAnalystChat(id);
      setChatId(data.id);
      setMessages([WELCOME_MSG, ...data.messages]);
      setSuggestStrategy(false);
      setEngagementOptions(null);
      setInput('');
      lastTickersRef.current = [];
      usedSourcesRef.current = new Set();
      if (window.innerWidth <= 768) setSidebarOpen(false);
    } catch {
      // silent
    }
  };

  const handleDeleteChat = async (id) => {
    try {
      await deleteAnalystChat(id);
      setChatList((prev) => prev.filter((c) => c.id !== id));
      if (chatId === id) startNewChat();
    } catch {
      // silent
    }
  };

  const handleRenameChat = async (id) => {
    if (!editTitleValue.trim()) { setEditingTitle(null); return; }
    try {
      const { data } = await updateAnalystChat(id, { title: editTitleValue.trim() });
      setChatList((prev) => prev.map((c) => (c.id === id ? { ...c, title: data.title } : c)));
    } catch {
      // silent
    }
    setEditingTitle(null);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    if (!isAuthenticated) {
      if (onRequiresSignup) onRequiresSignup('Sign up to use the AI Analyst.');
      return;
    }

    const userMsg = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setStatusText('');
    setEngagementOptions(null);

    try {
      const history = newMessages
        .filter((_, i) => i > 0)
        .map((m) => ({ role: m.role, content: m.content }));

      const token = localStorage.getItem('token');
      const res = await fetch(`/api/analyst/chat/stream/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Token ${token}` } : {}),
        },
        body: JSON.stringify({
          message: userMsg.content,
          history,
          chat_id: chatId || undefined,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail || 'Request failed';
        if (res.status === 429 || res.status === 403) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: detail,
              isLimitError: true,
            },
          ]);
          setLoading(false);
          setStatusText('');
          return;
        }
        throw new Error(detail);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const payload = JSON.parse(line.slice(6));

              if (eventType === 'status') {
                setStatusText(payload.message || '');
              } else if (eventType === 'complete') {
                setStatusText('');
                const assistantMsg = {
                  role: 'assistant',
                  content: payload.reply,
                  tickers: payload.tickers,
                  stockData: payload.stock_data,
                  earningsData: payload.earnings_data,
                };
                setMessages((prev) => [...prev, assistantMsg]);

                if (payload.chat_id && payload.chat_id !== chatId) {
                  setChatId(payload.chat_id);
                  loadChatList();
                } else {
                  loadChatList();
                }

                if (payload.suggest_strategy) setSuggestStrategy(true);

                // Build engagement options from sources. If this turn's ticker
                // inference came back empty, fall back to the last known tickers
                // instead of dropping ticker-gated chips (SEC/news, timeframe,
                // follow-ups) just because a follow-up message didn't restate them.
                const inferredTickers = payload.tickers || [];
                if (inferredTickers.length > 0) {
                  const prevTickers = lastTickersRef.current;
                  const tickersChanged = prevTickers.length !== inferredTickers.length
                    || !prevTickers.every((t) => inferredTickers.includes(t));
                  // Discussion pivoted to a different ticker — sources already
                  // pulled for the old one shouldn't suppress "add data" chips.
                  if (tickersChanged) usedSourcesRef.current = new Set();
                  lastTickersRef.current = inferredTickers;
                }
                const effectiveTickers = inferredTickers.length > 0 ? inferredTickers : lastTickersRef.current;

                // Track sources cumulatively across the whole conversation, not
                // just this turn, so a source already added doesn't get offered
                // again just because this turn's message didn't need it.
                (payload.sources_used || []).forEach((k) => usedSourcesRef.current.add(k));

                const available = payload.sources_available || [];
                const hasTickers = effectiveTickers.length > 0;
                const unusedSources = available.filter(
                  (s) => s.available && !usedSourcesRef.current.has(s.key) && (!s.requires_tickers || hasTickers)
                );
                const currentTimeframe = payload.timeframe || 'medium';

                if (unusedSources.length > 0 || hasTickers) {
                  setEngagementOptions({
                    unusedSources,
                    currentTimeframe,
                    tickers: effectiveTickers,
                  });
                }
              } else if (eventType === 'error') {
                setStatusText('');
                setMessages((prev) => [
                  ...prev,
                  { role: 'assistant', content: `\u26A0\uFE0F ${payload.detail || 'Something went wrong.'}` },
                ]);
              }
            } catch {
              // ignore malformed JSON
            }
            eventType = null;
          }
        }
      }
    } catch (err) {
      setStatusText('');
      const detail = err.message || 'Something went wrong. Please try again.';
      setMessages((prev) => [...prev, { role: 'assistant', content: `\u26A0\uFE0F ${detail}` }]);
    } finally {
      setLoading(false);
      setStatusText('');
    }
  };

  // Poll the persisted chat until the background generation with `genId` leaves
  // the "building" state, then adopt the server's messages (which carry the
  // permanent ready/failed item). Stops itself on completion or when unmounted.
  const pollForGeneratedStrategy = useCallback((cid, genId, attempt = 0) => {
    if (attempt > 60) return; // ~3 min ceiling; the notification + persisted item still cover it
    genPollRef.current = setTimeout(async () => {
      try {
        const { data } = await getAnalystChat(cid);
        const msgs = data?.messages || [];
        const gen = msgs.find((m) => m?.strategy_gen?.id === genId);
        if (gen && gen.strategy_gen.status !== 'building') {
          setChatId((curr) => {
            if (curr === cid) setMessages(msgs); // only adopt if still viewing this chat
            return curr;
          });
          return;
        }
      } catch { /* transient — keep polling */ }
      pollForGeneratedStrategy(cid, genId, attempt + 1);
    }, 3000);
  }, []);

  useEffect(() => () => { if (genPollRef.current) clearTimeout(genPollRef.current); }, []);

  const handleCreateStrategy = async () => {
    if (!chatId) {
      // Generation is persisted onto a saved chat; send a message first so one exists.
      setMessages((prev) => [...prev, { role: 'assistant', content: '⚠️ Ask me something first so I have a conversation to build from.' }]);
      return;
    }
    setGeneratingStrategy(true);
    setSuggestStrategy(false);

    const conversationSummary = messages
      .filter((m) => m.role !== 'system')
      .map((m) => `${m.role === 'user' ? 'User' : 'Analyst'}: ${m.content}`)
      .join('\n\n');

    const prompt =
      'Based on this analyst conversation, create a backtesting strategy that captures the key thesis discussed:\n\n' +
      conversationSummary +
      '\n\nCreate a concrete, executable trading strategy with specific indicators and conditions. ' +
      'If using AND/OR logic gates, each gate must have at least 2 inputs.';

    try {
      const { data } = await generateStrategyAsync({ prompt, chat_id: chatId });
      // Show the "being built" modal, and optimistically surface the persistent
      // building item so the user sees progress even before the first poll lands.
      setBuildModalOpen(true);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: '⏳ Building your strategy from this conversation…',
          strategy_gen: { id: data.gen_id, status: 'building', notification_id: data.notification_id },
        },
      ]);
      pollForGeneratedStrategy(chatId, data.gen_id);
    } catch (err) {
      const data = err.response?.data || {};
      const detail = data.detail || 'Failed to start strategy generation.';
      setMessages((prev) => [...prev, { role: 'assistant', content: `⚠️ ${detail}` }]);
      setSuggestStrategy(true);
    } finally {
      setGeneratingStrategy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // Auto-send follow-up messages from engagement chips
  const pendingFollowUp = useRef(null);
  const triggerFollowUp = useCallback((text) => {
    setEngagementOptions(null);
    pendingFollowUp.current = text;
    setInput(text);
  }, []);

  useEffect(() => {
    if (pendingFollowUp.current && input === pendingFollowUp.current && !loading) {
      pendingFollowUp.current = null;
      sendMessage();
    }
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, display: 'flex', overflow: 'hidden' }}>
      <Helmet>
        <title>AI Analyst — Algovex</title>
        <meta name="description" content="Chat with the Algovex AI analyst to get insights on your trading strategies, market conditions, and backtesting results." />
        <meta name="robots" content="noindex" />
      </Helmet>
      {sidebarOpen && (
        <div style={{
          position: window.innerWidth <= 768 ? 'absolute' : 'relative',
          top: 0,
          left: 0,
          width: window.innerWidth <= 768 ? '100%' : 260,
          height: window.innerWidth <= 768 ? '100%' : undefined,
          zIndex: window.innerWidth <= 768 ? 100 : undefined,
          background: 'var(--surface)',
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}>
          <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => { startNewChat(); if (window.innerWidth <= 768) setSidebarOpen(false); }} style={newChatBtnStyle}>+ New Chat</button>
            <button onClick={() => setSidebarOpen(false)} style={iconBtnStyle} title="Close sidebar">&times;</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {loadingChats && <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--muted)' }}>Loading...</div>}
            {chatList.map((c) => (
              <div
                key={c.id}
                style={{
                  padding: '8px 14px',
                  cursor: 'pointer',
                  background: chatId === c.id ? 'rgba(255,255,255,0.08)' : 'transparent',
                  borderLeft: chatId === c.id ? '3px solid var(--teal)' : '3px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {editingTitle === c.id ? (
                  <input
                    autoFocus
                    value={editTitleValue}
                    onChange={(e) => setEditTitleValue(e.target.value)}
                    onBlur={() => handleRenameChat(c.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRenameChat(c.id); if (e.key === 'Escape') setEditingTitle(null); }}
                    style={{ flex: 1, fontSize: 12, background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px' }}
                  />
                ) : (
                  <div onClick={() => loadChat(c.id)} style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: chatId === c.id ? 700 : 500, color: 'var(--text)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {c.title}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                      {new Date(c.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingTitle(c.id); setEditTitleValue(c.title); }}
                  style={tinyBtnStyle}
                  title="Rename"
                >&#9998;</button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteChat(c.id); }}
                  style={{ ...tinyBtnStyle, color: '#ef4444' }}
                  title="Delete"
                >&times;</button>
              </div>
            ))}
            {!loadingChats && chatList.length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--muted)' }}>No saved chats yet</div>
            )}
          </div>
        </div>
      )}

      {/* Main chat area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
        {/* Header */}
        <div className="analyst-header" style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          {!sidebarOpen && (
            <button onClick={() => setSidebarOpen(true)} style={iconBtnStyle} title="Open chat history">&#9776;</button>
          )}
          <div>
            <h2 style={{ margin: 0, fontSize: 20, color: 'var(--text)' }}>
              <span style={{ color: 'var(--teal)' }}>AI</span> Research Analyst
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted)' }}>
              Ask about any stock, sector, or strategy &mdash; powered by live market data
            </p>
          </div>
        </div>

        {/* Messages */}
        <div className="analyst-messages" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {messages.map((msg, i) => (
            <ChatBubble
              key={i}
              message={msg}
              onExport={(cs) => { track(Events.STRATEGY_EXPORT_OPENED, { source: 'analyst_chat' }); setExportTarget({ graph: cs.graph, name: cs.name }); }}
              onOpenBuilder={(cs) => onStrategyCreated && onStrategyCreated(cs)}
            />
          ))}

          {loading && (
            <div style={{ display: 'flex', gap: 8, padding: '12px 0' }}>
              <div style={avatarStyle('assistant')}>AI</div>
              <div style={{ ...bubbleBase, background: 'var(--card)', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', gap: 4, padding: '4px 0' }}>
                  <span style={dotStyle(0)} />
                  <span style={dotStyle(1)} />
                  <span style={dotStyle(2)} />
                </div>
                {statusText && (
                  <div style={{
                    fontStyle: 'italic',
                    fontSize: 12,
                    color: 'var(--muted)',
                    marginTop: 6,
                    transition: 'opacity 0.3s',
                  }}>
                    {statusText}
                  </div>
                )}
              </div>
            </div>
          )}

          {engagementOptions && !loading && (
            <div className="analyst-engagement" style={{
              margin: '12px 0 4px 44px',
              padding: '14px 16px',
              background: 'var(--surface)',
              borderRadius: 12,
              border: '1px solid var(--border)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
                Refine your analysis
              </div>

              {/* Unused data sources */}
              {engagementOptions.unusedSources.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Add more data
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {engagementOptions.unusedSources.map((src) => (
                      <button
                        key={src.key}
                        onClick={() => {
                          const tickerStr = engagementOptions.tickers.join(', ');
                          const prompts = {
                            stock: `Pull fresh market data and technicals for ${tickerStr}`,
                            macro: `Include macro economic indicators (Fed rates, CPI, GDP, unemployment) in the analysis`,
                            sec: `Add SEC filing data and insider trading activity for ${tickerStr}`,
                            news: `Include recent news headlines and sentiment analysis for ${tickerStr}`,
                            earnings: `Pull the earnings calendar and key fundamentals for ${tickerStr}`,
                            econ_calendar: 'What upcoming economic data releases should I watch for?',
                          };
                          triggerFollowUp(prompts[src.key] || `Add ${src.label} data to the analysis`);
                        }}
                        style={chipStyle}
                        title={src.description}
                      >
                        {src.icon} {src.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeframe adjustment */}
              {engagementOptions.tickers.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Adjust timeframe
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {[
                      { key: 'short', label: 'Short term', desc: '1-3 months' },
                      { key: 'medium', label: 'Medium term', desc: '6 months' },
                      { key: 'long', label: 'Long term', desc: '1-2 years' },
                    ].filter((t) => t.key !== engagementOptions.currentTimeframe).map((tf) => (
                      <button
                        key={tf.key}
                        onClick={() => {
                          const tickerStr = engagementOptions.tickers.join(', ');
                          triggerFollowUp(`Redo the analysis for ${tickerStr} with a ${tf.label.toLowerCase()} focus (${tf.desc})`);
                        }}
                        style={chipStyle}
                      >
                        ⏱️ {tf.label} <span style={{ fontSize: 11, color: 'var(--muted)' }}>({tf.desc})</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggested follow-ups */}
              {engagementOptions.tickers.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Keep exploring
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <button
                      onClick={() => triggerFollowUp(`What are the key risk factors for ${engagementOptions.tickers.join(', ')}?`)}
                      style={chipStyle}
                    >
                      ⚠️ Risk factors
                    </button>
                    <button
                      onClick={() => triggerFollowUp(`Compare ${engagementOptions.tickers.join(', ')} against their sector peers`)}
                      style={chipStyle}
                    >
                      🔄 Peer comparison
                    </button>
                    <button
                      onClick={() => triggerFollowUp(`What's the best entry point and price target for ${engagementOptions.tickers.join(', ')}?`)}
                      style={chipStyle}
                    >
                      🎯 Entry & targets
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {suggestStrategy && (
          <div className="analyst-strategy-banner" style={{
            flexShrink: 0,
            margin: '0 20px 12px',
            padding: '12px 16px',
            background: 'linear-gradient(135deg, rgba(20,184,166,0.16), rgba(20,184,166,0.05))',
            border: '1px solid var(--teal)',
            borderRadius: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                  📈 Strategy idea ready
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                  Build a backtestable strategy from the conversation so far.
                </div>
              </div>
              <button
                onClick={() => setSuggestStrategy(false)}
                disabled={generatingStrategy}
                title="Dismiss"
                style={{
                  background: 'none', border: 'none', color: 'var(--muted)', fontSize: 18,
                  cursor: generatingStrategy ? 'default' : 'pointer', padding: '0 4px', lineHeight: 1,
                  flexShrink: 0,
                }}
              >&times;</button>
            </div>
            <button
              className="analyst-strategy-cta"
              onClick={handleCreateStrategy}
              disabled={generatingStrategy}
              style={{
                alignSelf: 'flex-start',
                background: 'var(--teal)', color: '#fff', border: 'none',
                padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                cursor: generatingStrategy ? 'default' : 'pointer', opacity: generatingStrategy ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {generatingStrategy
                ? <><Spinner size={12} color="#fff" style={{ marginRight: 7 }} />Creating…</>
                : 'Create Backtest Strategy'}
            </button>
          </div>
        )}

        {/* Input */}
        <div className="analyst-input-bar" style={{
          flexShrink: 0, padding: '12px 20px 16px', borderTop: '1px solid var(--border)',
          display: 'flex', gap: 8,
        }}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about a stock, market trend, or strategy…"
            rows={1}
            style={{
              flex: 1, resize: 'none', padding: '10px 14px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--surface)',
              color: 'var(--text)', fontSize: 14, fontFamily: 'inherit', outline: 'none',
              minHeight: 42, maxHeight: 120, overflowY: 'auto',
            }}
            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            style={{
              background: 'var(--teal)', color: '#fff', border: 'none', padding: '0 20px',
              borderRadius: 8, fontSize: 14, fontWeight: 700, flexShrink: 0,
              cursor: loading || !input.trim() ? 'default' : 'pointer',
              opacity: loading || !input.trim() ? 0.5 : 1,
            }}
          >Send</button>
        </div>

        <style>{`
          @keyframes blink {
            0%, 80%, 100% { opacity: 0.3; }
            40% { opacity: 1; }
          }
          @media (max-width: 768px) {
            .analyst-input-bar {
              padding: 8px 10px 10px !important;
            }
            .analyst-header {
              padding: 10px 12px 8px !important;
            }
            .analyst-messages {
              padding: 10px 12px !important;
            }
            .analyst-engagement {
              margin-left: 0 !important;
            }
            .analyst-strategy-banner {
              margin: 0 12px 10px !important;
            }
            .analyst-strategy-cta {
              width: 100%;
            }
          }
        `}</style>
      </div>

      {exportTarget && (
        <ExportStrategyModal
          graph={exportTarget.graph}
          name={exportTarget.name}
          onClose={() => setExportTarget(null)}
          onRequiresSignup={onRequiresSignup}
        />
      )}

      {buildModalOpen && (
        <div
          onClick={() => setBuildModalOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14,
              maxWidth: 420, width: '100%', padding: '24px 22px', textAlign: 'center',
              boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <Spinner size={26} />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, color: 'var(--text)' }}>Building your strategy…</h3>
            <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
              This runs in the background — you can keep chatting or leave this page. We'll drop a
              notification the moment it's ready, and it'll appear right here in the conversation.
            </p>
            <button
              onClick={() => setBuildModalOpen(false)}
              style={{
                background: 'var(--teal)', color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}

// -- Styles & sub-components --

function LimitUpgradeLink() {
  return (
    <a
      href="/pricing"
      style={{ color: '#f85149', fontWeight: 700, fontSize: 12, textDecoration: 'underline', whiteSpace: 'nowrap' }}
    >
      View plans →
    </a>
  );
}

function ChatBubbleCannotBuild({ message }) {
  const [dismissed, setDismissed] = React.useState(false);
  if (dismissed) return null;
  return (
    <div style={{
      margin: '6px 0',
      padding: '12px 14px',
      background: 'rgba(251,191,36,0.08)',
      border: '1px solid rgba(251,191,36,0.35)',
      borderRadius: 10,
      fontSize: 13,
      color: 'var(--text)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>🤔</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, color: 'var(--amber)', marginBottom: 4 }}>
            Couldn't build this strategy with the current blocks
          </div>
          <div style={{ color: 'var(--muted)', lineHeight: 1.5, marginBottom: 10 }}>
            {message.content}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>Would you like to see this supported?</span>
            <button
              onClick={() => {
                setDismissed(true);
                if (message.onFeedback) message.onFeedback(
                  `Feature Request: ${(message.cannotBuildPrompt || '').slice(0, 120)}`,
                  `I asked the AI Analyst to create a strategy but it wasn't possible with the current blocks:\n\n"${message.cannotBuildPrompt}"\n\nAI response:\n${message.cannotBuildDetail}`
                );
              }}
              style={{ padding: '4px 12px', background: 'var(--amber)', color: 'var(--bg)', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}
            >
              Request this feature →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const newChatBtnStyle = {
  flex: 1, background: 'var(--teal)', color: '#fff', border: 'none',
  padding: '7px 0', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer',
};

const iconBtnStyle = {
  background: 'none', border: 'none', color: 'var(--muted)', fontSize: 18,
  cursor: 'pointer', padding: '2px 6px', lineHeight: 1,
};

const tinyBtnStyle = {
  background: 'none', border: 'none', color: 'var(--muted)', fontSize: 14,
  cursor: 'pointer', padding: '0 2px', lineHeight: 1, flexShrink: 0,
};

const chipStyle = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  color: 'var(--text)',
  padding: '6px 12px',
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  transition: 'border-color 0.2s, background 0.2s',
  whiteSpace: 'nowrap',
};

const bubbleBase = {
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 14,
  lineHeight: 1.6,
  maxWidth: '100%',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const avatarStyle = (role) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11,
  fontWeight: 800,
  flexShrink: 0,
  background: role === 'assistant' ? 'var(--teal)' : 'var(--blue, #3b82f6)',
  color: '#fff',
});

const dotStyle = (i) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'var(--muted)',
  animation: `blink 1.4s ${i * 0.2}s infinite`,
});

function ChatBubble({ message, onExport, onOpenBuilder }) {
  const navigate = useNavigate();
  const isUser = message.role === 'user';

  // Limit-error messages get a distinct styled banner instead of a chat bubble
  if (message.isLimitError) {
    return (
      <div style={{
        margin: '6px 0',
        padding: '10px 14px',
        background: 'rgba(248,81,73,0.10)',
        border: '1px solid rgba(248,81,73,0.35)',
        borderRadius: 10,
        fontSize: 13,
        color: '#f85149',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 16 }}>🚫</span>
        <span style={{ flex: 1 }}>{message.content}</span>
        <LimitUpgradeLink />
      </div>
    );
  }

  // Cannot-build messages get a distinct amber banner with a feedback CTA
  if (message.isCannotBuild) {
    return (
      <ChatBubbleCannotBuild message={message} />
    );
  }

  // Simple markdown-ish rendering
  const renderContent = (text) => {
    // Bold
    let html = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Italic
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    // Inline code
    html = html.replace(/`(.*?)`/g, '<code style="background:var(--surface);padding:1px 4px;border-radius:3px;font-size:12px">$1</code>');
    return html;
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '6px 0',
          flexDirection: isUser ? 'row-reverse' : 'row',
        }}
      >
        <div style={avatarStyle(message.role)}>{isUser ? 'U' : 'AI'}</div>
        <div
          style={{
            ...bubbleBase,
            background: isUser ? 'var(--teal)' : 'var(--card)',
            color: isUser ? '#fff' : 'var(--text)',
            border: isUser ? 'none' : '1px solid var(--border)',
          }}
          dangerouslySetInnerHTML={{ __html: renderContent(message.content) }}
        />
      </div>

      {!isUser && message.createdStrategy && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '2px 0 6px 44px' }}>
          <button
            onClick={() => onExport && onExport(message.createdStrategy)}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 700, borderRadius: 999,
              border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer',
            }}
          >⬇ Export code</button>
          <button
            onClick={() => onOpenBuilder && onOpenBuilder(message.createdStrategy)}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 700, borderRadius: 999,
              border: 'none', background: 'var(--teal)', color: 'var(--bg)', cursor: 'pointer',
            }}
          >Open in Builder →</button>
        </div>
      )}

      {/* Async analyst→builder generation. `strategy_gen` is persisted on the
          message, so the building/ready state survives navigating away and back. */}
      {!isUser && message.strategy_gen?.status === 'building' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 6px 44px',
          padding: '8px 14px', borderRadius: 999, border: '1px dashed var(--border)',
          background: 'var(--surface)', color: 'var(--muted)', fontSize: 12, fontWeight: 600,
          alignSelf: 'flex-start', width: 'fit-content',
        }}>
          <Spinner size={12} /> Building your strategy… hang tight or check your notifications.
        </div>
      )}
      {!isUser && message.strategy_gen?.status === 'ready' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '2px 0 6px 44px' }}>
          <button
            onClick={() => onExport && onExport(message.strategy_gen)}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 700, borderRadius: 999,
              border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer',
            }}
          >⬇ Export code</button>
          <button
            onClick={() => onOpenBuilder && onOpenBuilder({
              id: message.strategy_gen.strategy_id,
              name: message.strategy_gen.name,
              graph: message.strategy_gen.graph,
              description: message.strategy_gen.explanation,
            })}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 700, borderRadius: 999,
              border: 'none', background: 'var(--teal)', color: 'var(--bg)', cursor: 'pointer',
            }}
          >View strategy →</button>
        </div>
      )}

      {!isUser && message.tickers?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '0 0 4px 44px' }}>
          {message.tickers.map((t) => (
            <button
              key={t}
              onClick={() => navigate(`/stock/${t}`)}
              style={{
                padding: '3px 10px',
                fontSize: 11,
                fontWeight: 600,
                border: '1px solid var(--border)',
                borderRadius: 999,
                background: 'var(--surface)',
                color: 'var(--muted)',
                cursor: 'pointer',
              }}
            >
              {t} →
            </button>
          ))}
        </div>
      )}

      {!isUser && message.earningsData && !message.earningsData.error && message.tickers?.[0] && (
        <div style={{ marginLeft: 44 }}>
          <EarningsFundamentalsCard data={message.earningsData[message.tickers[0]]} />
        </div>
      )}
    </div>
  );
}
